import { ReactNode, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { WebSocketMessages } from 'constants/webSocketMessages'
import { useWebSockets, WebSocketProvider } from 'features/session/context'

import { setDocumentTitle } from 'shared/util'

import {
  Stack,
  StackProps,
  Container,
  Paper,
  Box,
  Tabs,
  Tab,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material'

import {
  PersonSearch as ConditionIcon,
  ListAlt as TargetListIcon,
  History as ProjectIcon,
  FileUpload as ImportIcon
} from '@mui/icons-material';

import { SearchResult, useGetSearch } from 'interface/search'
import { useListTargetList } from 'interface/search/targetList'

import { SearchHeader } from '../components/header'
import { SearchSideArea } from '../components/sideArea'
import { SearchStatusChip } from '../components/searchStatusChip'

import { SearchConditionPage } from '../components/conditionPage'
import { SearchTargetPage } from '../components/targetPage'
import { SearchProjectPage } from '../components/projectPage'
import { SearchImportPage } from '../components/importPage'

import { useUserSession } from 'features/session/context'
import { useTenant } from 'features/tenant/context'

import { BreadCrumbItem } from 'components/topBar/types'
import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { useSideMenu } from 'components/sideMenu/container'
import { useTopBar } from 'components/topBar/container'

type SearchTabNames = 'targetList' | 'project' | 'import' | 'conditions'

const SearchTabPanel = ({
  currentTab,
  tabValue,
  children,
  isSmallMedia
}: {
  currentTab: SearchTabNames
  tabValue: SearchTabNames
  children: ReactNode
  isSmallMedia: boolean
}) => (
  <Box
    role="tabpanel"
    hidden={currentTab !== tabValue}
    id={`facility-tabpanel-${currentTab}`}
    aria-labelledby={`facility-tabpanel-${currentTab}`}
    sx={isSmallMedia ? {
      width: '100%'
    } : {
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'absolute',
      left: 0,
      right: 0
    }}
  >
    {children}
  </Box>
)

type SearchHomePageProps = {
  searchId: number
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
  openSisetu: () => void
  openKojin: () => void
  openGrid: () => void
  openTargetList: (targetListId: number, isOpenGrid: boolean) => void
  openMrpProject: (projectId: number, isOpenGrid: boolean) => void
  openLvProject: (projectCode: string, isOpenGrid: boolean) => void
  openGuideLine: (guideLineId: number) => void
  openConvention: (conventionId: number) => void
  onDeleteSearch: () => void
} & StackProps
export const SearchHomePage = (props: SearchHomePageProps) => {
  const { searchId } = props

  const searchStatus = useGetSearch(searchId)

  if (searchStatus.loading) return <Loading/>
  if (searchStatus.error) return <ErrorInfo error={searchStatus.error}/>
  if (!searchStatus.data) return <></>

  return (
    <WebSocketProvider>
      <SearchHomePageCore
        search={searchStatus.data}
        reloadSearch={searchStatus.refetch}
        {...props}
      />
    </WebSocketProvider>
  )
}

const SearchHomePageCore = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
  search,
  reloadSearch,
  openSisetu,
  openKojin,
  openGrid,
  openTargetList,
  openMrpProject,
  openLvProject,
  openGuideLine,
  openConvention,
  onDeleteSearch,
  ...others
}: SearchHomePageProps & {
  search: SearchResult
  reloadSearch: () => void
}) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))

  const { loginToken, isCareNet, isGeneral } = useUserSession()
  const tenant = useTenant()
  const socket = useWebSockets()
  const targetListState = useListTargetList(search.searchId)

  const [params, setParams] = useSearchParams()
  const [tabValue, setTabValue] = useState(
    () => {
      const tabs = params.get("tabs")
      if (tabs && ['conditions', 'targetList', 'project'].includes(tabs)) {
        return tabs as SearchTabNames
      }
      return 'targetList'
    }
  )

  useEffect(() => setParams(
    (prev) => {
      prev.set("tabs", tabValue)
      return prev
    }
  ), [tabValue, setParams])

  useEffect(
    () => {
      socket?.on('connect', () => {
        socket.emit(WebSocketMessages.Login, { loginToken, searchId: search.searchId })

        socket.on(WebSocketMessages.Search.Removed, reloadSearch)
        socket.on(WebSocketMessages.Search.Updated, reloadSearch)
        socket.on(WebSocketMessages.Analytics.Start, reloadSearch)
        socket.on(WebSocketMessages.Analytics.Finished, reloadSearch)
        socket.on(WebSocketMessages.Analytics.Failed, reloadSearch)
      })
      return () => {
        socket?.removeAllListeners()
      }
    },
    [loginToken, socket, search, reloadSearch]
  )

  useEffect(
    () => {
      setDocumentTitle(search?.title, tenant.tenantName)
      const crumbs: BreadCrumbItem[] = []
      if (search?.manufacturer?.manufacturerName) {
        crumbs.push({
          label: search.manufacturer.manufacturerName,
          url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
        })
      }
      if (search?.title) {
        crumbs.push({ label: search.title })
      }
      setBreadCrumbs(crumbs)
      setActivePage('search')
    },
    [tenant, search, setBreadCrumbs, setActivePage]
  )
  return (
    <Stack overflow="auto" position="relative" {...others}>
      <Container
        maxWidth="xl"
        sx={{
          height: "100%",
          padding: isSmallMedia ? 0 : 2
        }}
      >
        <Paper
          square={isSmallMedia}
          sx={{
            height: "100%",
            overflow: isSmallMedia ? "auto" : "hidden"
          }}
        >
          <Stack direction="column" height="100%">
            <SearchHeader
              search={search}
              onChangeSearch={reloadSearch}
              onDeleteSearch={onDeleteSearch}
              isSmallMedia={isSmallMedia}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                position: "sticky",
                top: 0, zIndex: 10,
                background: theme.palette.background.paper
              }}
            />
            <Stack
              flexGrow={1}
              direction={isSmallMedia ? "column" : "row"}
              overflow={isSmallMedia ? undefined : "hidden"}
            >
              <SearchSideArea
                search={search} 
                flexShrink={0}
                onChangeSearch={reloadSearch}
                boxSizing="border-box"
                width={isSmallMedia ? "100%" : "30%"}
                sx={{
                  borderRight: isSmallMedia ? undefined : 1,
                  borderBottom: isSmallMedia ? 1 : undefined,
                  borderColor: 'divider'
                }}
              />
              <Stack
                flexGrow={1}
                direction="column"
                boxSizing="border-box"
                height={isSmallMedia ? undefined : "100%"}
                overflow={isSmallMedia ? undefined : "hidden"}
              >
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    paddingX: 2,
                    position: "sticky",
                    top: 0, zIndex: 12,
                    background: theme.palette.background.paper
                  }}
                >
                  <Tabs
                    value={tabValue}
                    onChange={(_, newValue) => setTabValue(newValue)}
                  >
                    <Tab
                      value='targetList'
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <TargetListIcon/>
                          <Typography variant="body2">分析結果/ターゲット</Typography>
                        </Stack>
                      }
                    />
                    
                    {isCareNet && (
                      <Tab
                        value='project'
                        label={
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ProjectIcon/>
                            <Typography variant="body2">過去の配信対象</Typography>
                          </Stack>
                        }
                      />
                    )}

                    <Box flexGrow={1}/>

                    {isGeneral && (
                      <Tab
                        value='import'
                        label={
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ImportIcon/>
                            <Typography variant="body2">取り込み</Typography>
                          </Stack>
                        }
                      />
                    )}

                    <Tab
                      value='conditions'
                      label={
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <ConditionIcon/>
                            <Typography variant="body2">分析</Typography>
                          </Stack>
                          <SearchStatusChip status={search.status} size="small" />
                        </Stack>
                      }
                    />
                  </Tabs>
                </Box>
                <Box flexGrow={1} position="relative" zIndex={11} sx={{ background: theme.palette.background.paper }}>
                  <SearchTabPanel
                    currentTab="targetList"
                    tabValue={tabValue}
                    isSmallMedia={isSmallMedia}
                  >
                    <SearchTargetPage
                      position="relative"
                      height={isSmallMedia ? undefined : "100%"}
                      search={search}
                      targetListState={targetListState}
                      onChangeSearch={reloadSearch}
                      openSisetu={() => openSisetu()}
                      openKojin={() => openKojin()}
                      openGrid={() => openGrid()}
                      openTargetList={openTargetList}
                    />
                  </SearchTabPanel>

                  <SearchTabPanel
                    currentTab="project"
                    tabValue={tabValue}
                    isSmallMedia={isSmallMedia}
                  >
                    <SearchProjectPage
                      position="relative"
                      height={isSmallMedia ? undefined : "100%"}
                      isSmallMedia={isSmallMedia}
                      search={search}
                      openMrpProject={openMrpProject}
                      openLvProject={openLvProject}
                    />
                  </SearchTabPanel>

                  <SearchTabPanel
                    currentTab="import"
                    tabValue={tabValue}
                    isSmallMedia={isSmallMedia}
                  >
                    <SearchImportPage
                      position="relative"
                      search={search}
                    />
                  </SearchTabPanel>

                  <SearchTabPanel
                    currentTab="conditions"
                    tabValue={tabValue}
                    isSmallMedia={isSmallMedia}
                  >
                    <SearchConditionPage
                      position="relative"
                      height={isSmallMedia ? undefined : "100%"}
                      search={search}
                      openGuideLine={openGuideLine}
                      openConvention={openConvention}
                      onChangeSearch={reloadSearch}
                    />
                  </SearchTabPanel>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Stack>
  )
}